import { useState } from "react";
import { useAuth } from "@/context/auth-context";
// Signups disabled 2026-07-14 — re-add Link import when restoring the sign-up link below.
import { useNavigate } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import Button from "@/components/Button";
import Input from "@/components/Input";
import { signInSchema, type SignInFormData } from "@/schemas/auth";
import { requestPasswordReset } from "@/lib/pocketbase";
import { z } from "zod";

import signin from "../assets/imgs/signin.png";

const SignInPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [validationErrors, setValidationErrors] = useState<{
    email?: string;
    password?: string;
  }>({});
  const [showForgotPasswordForm, setShowForgotPasswordForm] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [isRequestingReset, setIsRequestingReset] = useState(false);

  const { signIn, signInWithGoogle, isSigningIn, isSigningInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const validateForm = () => {
    try {
      const formData: SignInFormData = { email, password };
      signInSchema.parse(formData);
      setValidationErrors({});
      return true;
    } catch (err) {
      if (err instanceof z.ZodError) {
        const errors: typeof validationErrors = {};
        err.errors.forEach((error) => {
          if (error.path[0]) {
            errors[error.path[0] as keyof typeof errors] = error.message;
          }
        });
        setValidationErrors(errors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await signIn(email, password);
      enqueueSnackbar("Successfully signed in!", { variant: "success" });
      navigate({ to: "/dashboard" });
    } catch (err: any) {
      const errorMessage = err.message || "Sign in failed";
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  const handleShowForgotPassword = () => {
    setForgotPasswordEmail(email);
    setShowForgotPasswordForm(true);
  };

  const handleBackToSignIn = () => {
    setShowForgotPasswordForm(false);
    setValidationErrors({});
  };

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      enqueueSnackbar("Successfully signed in with Google!", { variant: "success" });
      navigate({ to: "/dashboard" });
    } catch (err: any) {
      const errorMessage = err.message || "Google sign in failed";
      enqueueSnackbar(errorMessage, { variant: "error" });
    }
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!forgotPasswordEmail) {
      enqueueSnackbar("Please enter your email address", { variant: "error" });
      return;
    }

    try {
      setIsRequestingReset(true);
      await requestPasswordReset(forgotPasswordEmail);
      enqueueSnackbar("Password reset email sent! Check your inbox.", {
        variant: "success",
      });
      setShowForgotPasswordForm(false);
    } catch (err: any) {
      const errorMessage = err.message || "Failed to send password reset email";
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setIsRequestingReset(false);
    }
  };

  return (
    <div className="w-full flex flex-col signin-page">
      {/* <Link to="/" className="w-fit">
        <Button className="m-8">Close</Button>
      </Link> */}
      <div className="flex flex-col lg:flex-row justify-between">
        <img src={signin} className="lg:w-1/2" />
        <div className="flex flex-col justify-center py-6 lg:py-0 px-5 lg:px-12 bg-white lg:w-1/2">
          {!showForgotPasswordForm ? (
            <>
              <h1 className="font-bold text-[24px] lg:text-[64px]">
                Welcome back
              </h1>
              <h2 className="font-bold text-[24px]">
                Enter your log in credentials
              </h2>
              <form
                onSubmit={handleSubmit}
                className="w-full mt-4 lg:mt-12 flex flex-col gap-y-4"
              >
                <Input
                  type="email"
                  label="Email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (validationErrors.email) {
                      setValidationErrors((prev) => ({
                        ...prev,
                        email: undefined,
                      }));
                    }
                  }}
                  error={validationErrors.email}
                />
                <Input
                  type="password"
                  label="Password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (validationErrors.password) {
                      setValidationErrors((prev) => ({
                        ...prev,
                        password: undefined,
                      }));
                    }
                  }}
                  error={validationErrors.password}
                />
                <button
                  type="button"
                  onClick={handleShowForgotPassword}
                  className="text-action-500 hover:text-action-600 underline text-sm"
                >
                  Forgot password?
                </button>

                <button className="dark-blue-button" disabled={isSigningIn}>
                  {isSigningIn ? "Logging in" : "Log in"}
                </button>

                <div className="flex items-center gap-4 my-4">
                  <div className="flex-1 h-px bg-gray-300"></div>
                  <span className="text-gray-500 text-sm">OR</span>
                  <div className="flex-1 h-px bg-gray-300"></div>
                </div>

                <p className="text-gray-700 text-sm text-center font-semibold">Google accounts require age 13+</p>

                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={isSigningInWithGoogle}
                  className="flex items-center justify-center gap-3 w-full py-3 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  {isSigningInWithGoogle ? "Signing in..." : "Continue with Google"}
                </button>

                {/* Signups disabled 2026-07-14 — restore to re-enable.
                <Link
                  to="/sign-up"
                  className="text-action-500 hover:text-action-600 underline"
                >
                  Don't have an account? Sign Up
                </Link> */}
              </form>
            </>
          ) : (
            <>
              <h1 className="font-bold text-[64px]">Reset Password</h1>
              <h2 className="font-bold text-[24px]">
                Enter your email to receive a reset link
              </h2>
              <form
                onSubmit={handleForgotPasswordSubmit}
                className="w-full mt-12 flex flex-col gap-y-4 items-center"
              >
                <Input
                  type="email"
                  label="Email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                />
                <Button className="w-full" disabled={isRequestingReset}>
                  {isRequestingReset ? "Sending..." : "Send Reset Link Email"}
                </Button>
                <button
                  type="button"
                  onClick={handleBackToSignIn}
                  className="text-action-500 hover:text-action-600 underline text-sm"
                >
                  Back to Sign In
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignInPage;
